import { CommonListParams } from "@/types/common";
import request, { AxiosResponseBody } from ".";
import { QuotationRequest, QuotationRequestStatusEnum, Requirement, RequirementAdd, RequirementAddItem, RequirementConfirm, RequirementConfirmAdd, RequirementConfirmShowEnum, RequirementConfirmTypeEnum, RequirementConfirmValueEnum, RequirementItem, RequirementStatusEnum, RequirementTemplate, RequirementTemplateAdd } from "@/types/requirement";
import { getStorage } from "@/utils/storage";
import { isString, isStringEmpty } from "@/utils/checkTool";
import { QuotationRequestItem } from "@/types/quotationRequestItem";
import { PurchaseQuotationRequest } from "@/types/purchase";
import { InventoryLotItem, OrderV2 } from "@/types/inventoryV2";

const url = "/wms/requirement";
const quotatonUrl = "/wms/quotationRequest";
const tempUrl = '/wms/requirement/template'

interface ConfirmPathProps {
  id: string;
  type: RequirementConfirmTypeEnum;
}

interface ListPathProps {
  value: RequirementConfirmValueEnum;
  type: RequirementConfirmTypeEnum;
  show?: RequirementConfirmShowEnum;
}

type RequirementUpdateKey = 'status'
const apis = {
  add: `${url}/add`,
  list: `${url}/list`,
  quotationList: `${quotatonUrl}/list`,
  quotationListIsSup: (isSupplier: string) => `${quotatonUrl}/isSupplierOrder/${isSupplier}/list`,
  quotationListIsSupTimed: (isSupplier: string) => `${quotatonUrl}/isSupplierOrder/${isSupplier}/list/timeout`,
  handleSelect: (id: string) => `${quotatonUrl}/${id}/select`,
  fetch: (id: string) => `${url}/${id}/fetch`,
  getRequirement: (id: string) => `${url}/${id}/get`,
  items: (id: string) => `${url}/${id}/items`,
  update: (id: string) => `${url}/${id}/update`,
  updateByKey: (id: string, key: RequirementUpdateKey) => `${url}/${id}/${key}/update`,
  confirm: ({ id, type }: ConfirmPathProps) => `${url}/${id}/${type}/confirm`,
  confirms: (id: string) => `${url}/${id}/confirms`,
  itemUpdate: (id: string) => `${url}/item/${id}/update`,
  itemsUpdate: `${url}/items/update`,
  itemsRm: `${url}/item/delete`,
  itemAdds: (id: string) => `${url}/${id}/item/adds`,
  tempAdd: (id: string) => `${tempUrl}/${id}/add`,
  tempSave: (id: string) => `${tempUrl}/${id}/save`,
  tempFetch: (id: string) => `${tempUrl}/${id}/fetch`,
  requirementGenerate: (id: string) => `${url}/${id}/quotationRequest/generate`,
  fetchQuotationRequests: (id: string) => `${url}/item/${id}/quotationRequests`,
  fetchAllQuotationRequests: (id: string) => `${url}/${id}/quotationRequests`,
  addQuotationRequest: (id: string, status: QuotationRequestStatusEnum) => `${url}/item/${id}/quotationRequest/status/${status}/generate`,
  // status 有值时拼接 /status/{status}，为空时调用不带状态的创建路径。
  createSupplierQuotationRequest: (
    id: string,
    code: string,
    status?: QuotationRequestStatusEnum | null
  ) => `${url}/item/${id}/supplier/${code}/quotationRequest${status != null ? `/status/${status}` : ""}/create`,
  itemsQuotationRequestGenerate: `${url}/items/quotationRequest/generate`,
  finishQuotationRequest: (id: string) => `${url}/${id}/quotationRequest/finish`,
  selectedQuotationRequest: (id: string) => `${url}/${id}/quotationRequest/selected`,
  inventoryOrders: (id: string) => `${url}/${id}/inventoryOrders`,
  specialOut: (id: string) => `${url}/${id}/specializedLots`,
  specialOutDo: (id: string) => `${url}/${id}/outOrder/do`,
  batchItems: `${url}/items`,
};

export const add = (addInfo: RequirementAdd) => {
  return new Promise<Requirement>((resolve, reject) => {
    return request<Requirement, RequirementAdd>(apis.add, {
      params: { body: addInfo },
      requestConfig: { headers: { org_id: getStorage('curOrgId') } }
    })
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export const update = (id: string, body: Partial<RequirementAdd>) => {
  return new Promise<Requirement>((resolve, reject) => {
    return request<Requirement, Partial<RequirementAdd>>(apis.update(id), {
      params: { body }
    })
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export interface UpdateByKey {
  key: RequirementUpdateKey;
  value: RequirementStatusEnum;
}
export const updateByKey = (id: string, props: UpdateByKey) => {
  return new Promise<void>((resolve, reject) => {
    if (
      (!isString(id) || isStringEmpty(id)) &&
      props.key &&
      !isString(props.key)
    ) {
      return reject(10012);
    }
    request<void, RequirementStatusEnum>(apis.updateByKey(id, props.key), {
      params: { body: props.value },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const fetch = (id: string) => {
  return new Promise<Requirement>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<Requirement, string>(apis.fetch(id))
      .then(({ body: detail }) => resolve(detail))
      .catch((err) => reject(err));
  });
};

export const getRequirement = (id: string) => {
  return new Promise<Requirement>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<Requirement, string>(apis.getRequirement(id))
      .then(({ body: detail }) => resolve(detail))
      .catch((err) => reject(err));
  });
};

export const items = (id: string) => {
  return new Promise<RequirementItem[]>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<RequirementItem[], string>(apis.items(id))
      .then(({ body }) => resolve(body || []))
      .catch((err) => reject(err));
  });
};

export const confirm = (pathUrl: ConfirmPathProps, data: RequirementConfirmAdd) => {
  return new Promise<void>((resolve, reject) => {
    return request<void, RequirementConfirmAdd>(apis.confirm(pathUrl), {
      params: { body: data },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export const confirms = (id: string, data: Array<RequirementConfirm>) => {
  return new Promise<void>((resolve, reject) => {
    return request<void, Array<RequirementConfirm>>(apis.confirms(id), {
      params: { body: data },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export enum QuotationRequestInts {
  MANUAL,
  AUTO
}

export const fetchQuotationRequests = (id: string, ints?: QuotationRequestInts[]) => {
  return new Promise<QuotationRequest[]>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<QuotationRequest[], QuotationRequestInts[]>(apis.fetchQuotationRequests(id), { params: { ints: ints } })
      .then(({ body }) => resolve(body || []))
      .catch((err) => reject(err));
  });
};

export const fetchAllQuotationRequests: (id: string, params?: CommonListParams<string>) => Promise<AxiosResponseBody<Array<QuotationRequest>>> = (id, params) => {
  return new Promise((resolve, reject) => {
    request<Array<QuotationRequest>, string>(apis.fetchAllQuotationRequests(id), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export interface QuotationRequestsParams {
  deadline: number;
  mrd: number;
  supplierPrice: number | null;
  supplierTime: number;
  mpq: string;
  days: number;
  strs?: string[];
  ddl: number | null;
  remark?: string;
  spareCount: string;
  spareRate: string;
}

export const addQuotationRequest = (id: string, status: QuotationRequestStatusEnum, data: QuotationRequestsParams, strs: string[]) => {
  return new Promise<QuotationRequest[]>((resolve, reject) => {
    return request<QuotationRequest[], QuotationRequestsParams>(apis.addQuotationRequest(id, status), {
      params: { body: data, strs: strs },
    })
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

// 供应商由路径中的 code 确定，请求体只承载本次报价数据。
export const createSupplierQuotationRequest = (
  id: string,
  code: string,
  data: QuotationRequestsParams,
  status?: QuotationRequestStatusEnum | null
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    request<void, QuotationRequestsParams>(
      apis.createSupplierQuotationRequest(id, code, status),
      { params: { body: data } }
    )
      .then(() => resolve())
      .catch(err => reject(err));
  });
};

interface QuotationListParams {
  keyword: string;
}

export const quotationListIsSup = (isSupplier: string, params: CommonListParams) => {
  return new Promise<CommonListParams<Array<PurchaseQuotationRequest>>>((resolve, reject) => {
    request<Array<PurchaseQuotationRequest>, string>(apis.quotationListIsSup(isSupplier), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const handleSelect = (id: string,) => {
  return new Promise<QuotationRequest>((resolve, reject) => {
    request<QuotationRequest>(apis.handleSelect(id))
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const quotationListIsSupTimed = (isSupplier: string, params: CommonListParams) => {
  return new Promise<CommonListParams<Array<PurchaseQuotationRequest>>>((resolve, reject) => {
    request<Array<PurchaseQuotationRequest>, string>(apis.quotationListIsSupTimed(isSupplier), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const quotationList = (params: CommonListParams) => {
  return new Promise<CommonListParams<Array<QuotationRequest>>>((resolve, reject) => {
    request<Array<QuotationRequest>, string>(apis.quotationList, { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

interface ListParams {
  keyword: string;
  states: Array<ListPathProps>;
  status: number;
  mrd?: number
}


export const list = (params: CommonListParams<ListParams>) => {
  let paramRes: CommonListParams<ListParams> = { ...params };
  if (!!paramRes?.body?.keyword) {
    let kw = paramRes.body.keyword.trim();
    let strs: string[] = []
    if (kw.includes(' ')) {
      strs = kw.split(' ')
      paramRes.body.keyword = ''
      paramRes.strs = strs
    }
  }
  return new Promise<CommonListParams<Array<Requirement>>>((resolve, reject) => {
    request<Array<Requirement>, ListParams>(apis.list, { params: paramRes })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const itemUpdate = (id: string, body: Partial<RequirementAddItem>) => {
  return new Promise<RequirementItem>((resolve, reject) => {
    request<RequirementItem, Partial<RequirementAddItem>>(apis.itemUpdate(id), { params: { body } })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export const itemAdds = (id: string, body: Array<RequirementAddItem>) => {
  return new Promise<Array<RequirementItem>>((resolve, reject) => {
    request<Array<RequirementItem>, Array<RequirementAddItem>>(apis.itemAdds(id), { params: { body } })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export const itemsUpdate = (body: Array<Partial<RequirementItem>>) => {
  return new Promise<void>((resolve, reject) => {
    request<void, Array<Partial<RequirementItem>>>(apis.itemsUpdate, { params: { body } })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export const itemsRm = (body: Array<string>) => {
  return new Promise<void>((resolve, reject) => {
    request<void, Array<string>>(apis.itemsRm, { params: { body } })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export const tempAdd = (id: string, addInfo: RequirementTemplateAdd) => {
  return new Promise<RequirementTemplate>((resolve, reject) => {
    return request<RequirementTemplate, RequirementTemplateAdd>(apis.tempAdd(id), {
      params: { body: addInfo },
    })
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export const tempSave = (id: string, addInfo: RequirementTemplateAdd) => {
  return new Promise<RequirementTemplate>((resolve, reject) => {
    return request<RequirementTemplate, RequirementTemplateAdd>(apis.tempSave(id), {
      params: { body: addInfo },
    })
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export const tempFetch = (id: string) => {
  return new Promise<RequirementTemplate>((resolve, reject) => {
    return request<RequirementTemplate>(apis.tempFetch(id))
      .then(res => resolve(res.body))
      .catch((err) => reject(err));
  })
};

export interface RequirementGenerateParams {
  deadline: number;
  mrd: number;
  supplierPrice: string; // 供应商价格
  supplierTime: string; // 供应商齐料时间
}
const requirementGenerate = (id: string, params: RequirementGenerateParams) => {
  return new Promise<CommonListParams<Array<QuotationRequestItem>>>((resolve, reject) => {
    request<Array<QuotationRequestItem>, RequirementGenerateParams>(apis.requirementGenerate(id), { params: { body: params } })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
}

const itemsQuotationRequestGenerate = (params: RequirementGenerateParams, itemIds: Array<string>) => {
  return new Promise<CommonListParams<Array<QuotationRequestItem>>>((resolve, reject) => {
    request<Array<QuotationRequestItem>, RequirementGenerateParams>(apis.itemsQuotationRequestGenerate, { params: { body: params, strs: itemIds } })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
}

const selectedQuotationRequest = (id: string) => {
  return request<Array<QuotationRequestItem>, RequirementGenerateParams>(apis.selectedQuotationRequest(id))
    .then((res) => Promise.resolve(res))
    .catch((err) => Promise.reject(err));
}

export const finishQuotationRequest = (id: string) => {
  return new Promise<Requirement>((resolve, reject) => {
    request<Requirement, undefined>(apis.finishQuotationRequest(id))
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  })
}


export const inventoryOrders = (id: string, params?: CommonListParams) => {
  return new Promise<CommonListParams<Array<OrderV2>>>((resolve, reject) => {
    request<Array<OrderV2>, string>(apis.inventoryOrders(id), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const specialOutlots = (id: string, params: CommonListParams) => {
  return new Promise<CommonListParams<Array<InventoryLotItem>>>((resolve, reject) => {
    request<Array<InventoryLotItem>, string>(apis.specialOut(id), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const specialOutDo = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void>(apis.specialOutDo(id))
      .then(() => resolve())
      .catch(() => reject());
  })
};

export const batchItems = (body: Array<string>) => {
  return new Promise<RequirementItem[]>((resolve, reject) => {
    request<Array<RequirementItem>, Array<string>>(apis.batchItems, { params: { body } })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  })
};

const exportObject = {
  add,
  update,
  updateByKey,
  confirm,
  confirms,
  quotationList,
  quotationListIsSup,
  quotationListIsSupTimed,
  list,
  fetch,
  items,
  itemUpdate,
  itemsUpdate,

  tempAdd,
  tempSave,
  tempFetch,

  requirementGenerate,
  itemsQuotationRequestGenerate,
  finishQuotationRequest,
  selectedQuotationRequest,
  fetchAllQuotationRequests,
  inventoryOrders
};

export type { ListParams, QuotationListParams, ListPathProps };

export default exportObject;
